// laas-editor.component.ts
import { Component, forwardRef, Input, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NG_VALUE_ACCESSOR, ControlValueAccessor } from '@angular/forms';
import { Nullable } from 'primeng/ts-helpers';
import Quill from 'quill';
import { MessageService } from 'primeng/api';
import { HelpCenterImageService } from '../../service/help-center-image.service';

@Component({
  selector: 'laas-editor',
  templateUrl: './laas-editor.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LaasEditorComponent),
      multi: true,
    },
  ],
})
export class LaasEditorComponent implements ControlValueAccessor, AfterViewInit {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;

  @Input() readonly: boolean = false;
  @Input() style: { [klass: string]: any } | null | undefined;
  @Input() permissionCode?: string;

  fontSizes: string[] = ["12px","14px","16px","18px","20px","24px","30px","36px","48px","60px","72px","96px"];

  fonts: {label: string, value: string}[] = [
    { label: "Font.Family.Arial", value: "arial" },
    { label: "Font.Family.Verdana", value: "verdana" },
    { label: "Font.Family.TimesNew", value: "times" },
    { label: "Font.Family.Georgia", value: "georgia" },
    { label: "Font.Family.CourierNew", value: "courier" },
    { label: "Font.Family.JhengHei", value: "jhenghei" },
    { label: "Font.Family.YaHei", value: "yahei" },
    { label: "Font.Family.PMingLiU", value: "pmingliu" },
    { label: "Font.Family.PingFangSC", value: "pingfang-sc" },
    { label: "Font.Family.PingFangTC", value: "pingfang-tc" },
    { label: "Font.Family.Heiti", value: "heiti" },
    { label: "Font.Family.Songti", value: "songti" },
  ];

  value: Nullable<string>;
  onModelChange: Function = () => {};
  onModelTouched: Function = () => {};

  private quillInstance: any = null;
  private mutationObserver: MutationObserver | null = null;
  private isInitialized = false;
  private pendingValue: string | null = null;
  private imageBlotRegistered = false;

  constructor(
    private imageService: HelpCenterImageService,
    private messageService: MessageService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.registerCustomImageBlot();

    var SizeStyle = Quill.import('attributors/style/size');
    var FontClass = Quill.import('attributors/class/font');
    SizeStyle.whitelist = [...this.fontSizes];
    FontClass.whitelist = this.fonts.map(font => font.value);
    Quill.register(SizeStyle, true);
    Quill.register(FontClass, true);
  }

  private registerCustomImageBlot(): void {
    if (this.imageBlotRegistered) {
      return;
    }

    try {
      const BlockEmbed = Quill.import('blots/block/embed');
      const isProtectedImageUrl = (url: string) =>
        url.includes('/HelpCenterImage/images/') || url.includes('/guide-center/v1/images/');

      class ImageBlot extends BlockEmbed {
        static create(value: any) {
          const node = super.create(value);
          let src = '';
          let originalSrc = '';

          if (typeof value === 'string') {
            if (isProtectedImageUrl(value)) {
              src = 'about:blank';
              originalSrc = value;
            } else {
              src = value;
              originalSrc = value;
            }
          } else if (value && typeof value === 'object') {
            src = value.src || '';
            originalSrc = value.originalSrc || src;
          }

          if (src) {
            node.setAttribute('src', src);
          }
          if (originalSrc) {
            node.setAttribute('data-original-src', originalSrc);
          }
          node.setAttribute('style', 'max-width: 100%; height: auto; display: block; margin: 1rem 0; border-radius: 6px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);');
          node.setAttribute('data-styled', 'true');
          return node;
        }

        static value(node: any) {
          return node.getAttribute('data-original-src') || node.getAttribute('src');
        }

        format(name: string, value: any) {
          if (name === 'style') {
            return;
          }
          super.format(name, value);
        }
      }

      (ImageBlot as any).blotName = 'image';
      (ImageBlot as any).tagName = 'img';

      Quill.register(ImageBlot, true);
      this.imageBlotRegistered = true;
    } catch (error) {
      console.warn('Failed to register custom ImageBlot:', error);
    }
  }

  ngAfterViewInit(): void {
    // 預留
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
  }

  writeValue(value: any): void {
    this.value = this.prepareEditorHtmlForProtectedImages(value);

    if (!this.quillInstance) {
      this.pendingValue = value;
      return;
    }

    if (this.isInitialized) {
      return;
    }

    if (value) {
      setTimeout(() => {
        this.ensureAllImagesStyled();
        this.convertEditorImagesToBlobs();
      }, 100);
    }
  }

  registerOnChange(fn: any): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onModelTouched = fn;
  }

  onEditorChange(event: any) {
    if (this.quillInstance) {
      this.value = this.getPersistedHtml();
    }
    this.onModelChange(this.value);
  }

  onEditorInit(event: any) {
    this.quillInstance = event.editor;

    if (this.pendingValue) {
      this.pendingValue = null;
    }

    if (!this.readonly) {
      this.setupImageHandler();
      this.disableBase64ImagePaste();
    }

    this.setupImageStyleObserver();

    setTimeout(() => {
      this.ensureAllImagesStyled();
      this.convertEditorImagesToBlobs();
      this.isInitialized = true;
    }, 150);
  }

  private setupImageStyleObserver(): void {
    if (!this.quillInstance) return;

    const editorRoot = this.quillInstance.root;

    this.mutationObserver = new MutationObserver((mutations) => {
      let needsStyleFix = false;
      let needsBlobConversion = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.target.nodeName === 'IMG') {
          const img = mutation.target as HTMLImageElement;
          if (mutation.attributeName === 'style') {
            const currentStyle = img.getAttribute('style') || '';
            if (!currentStyle.includes('max-width: 100%')) {
              needsStyleFix = true;
            }
          }
        }

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeName === 'IMG') {
              needsStyleFix = true;
              needsBlobConversion = true;
            }
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              if (element.querySelectorAll('img').length > 0) {
                needsStyleFix = true;
                needsBlobConversion = true;
              }
            }
          });
        }
      });

      if (needsStyleFix) {
        requestAnimationFrame(() => {
          this.ensureAllImagesStyled();
        });
      }

      if (needsBlobConversion) {
        requestAnimationFrame(() => {
          this.convertEditorImagesToBlobs();
        });
      }
    });

    this.mutationObserver.observe(editorRoot, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: true
    });
  }

  private ensureAllImagesStyled(): void {
    if (!this.quillInstance) return;

    const images = this.quillInstance.root.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      const currentStyle = img.getAttribute('style') || '';
      const requiredStyle = 'max-width: 100%; height: auto; display: block; margin: 1rem 0; border-radius: 6px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);';

      if (!currentStyle.includes('max-width: 100%')) {
        img.setAttribute('style', requiredStyle);
        img.setAttribute('data-styled', 'true');
      }
    });
  }

  /**
   * 將編輯器內的圖片 URL 轉換成 blob URL（即時預覽）
   */
  private convertEditorImagesToBlobs(): void {
    if (!this.quillInstance) return;

    const images = this.quillInstance.root.querySelectorAll('img');

    images.forEach((img: HTMLImageElement) => {
      const src = img.getAttribute('src');
      const originalSrc = img.getAttribute('data-original-src');

      if (img.getAttribute('data-blob-loading') === 'true') {
        img.removeAttribute('data-blob-loading');
      }

      if (!src || src.startsWith('blob:')) {
        return;
      }

      const targetUrl = originalSrc || src;
      if (this.isProtectedImageUrl(targetUrl)) {
        this.loadImageAsBlob(targetUrl, img);
      }
    });
  }

  private loadImageAsBlob(url: string, imgElement: HTMLImageElement): void {
    imgElement.setAttribute('data-original-src', url);
    imgElement.setAttribute('data-blob-loading', 'true');
    const requestUrl = this.resolveProtectedImageUrl(url);
    this.http.get(requestUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const blobUrl = URL.createObjectURL(blob);
        imgElement.src = blobUrl;
        imgElement.removeAttribute('data-blob-loading');
      },
      error: (err) => {
        imgElement.removeAttribute('data-blob-loading');
        console.error('Failed to load image in editor:', url, err);
      }
    });
  }
  private disableBase64ImagePaste(): void {
    if (!this.quillInstance) return;

    const quill = this.quillInstance;

    quill.clipboard.addMatcher('IMG', (node: any, delta: any): any => {
      return { ops: [] };
    });

    const editorElement = quill.root;

    editorElement.addEventListener('drop', (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const file = files[0];
        if (file.type.startsWith('image/')) {
          this.messageService.add({
            severity: 'warn',
            summary: '提示',
            detail: '請使用工具列的圖片按鈕上傳圖片'
          });
        }
      }
    }, true);

    editorElement.addEventListener('dragover', (e: DragEvent) => {
      e.preventDefault();
    }, true);
  }

  private setupImageHandler() {
    if (!this.quillInstance) return;

    const toolbar = this.quillInstance.getModule('toolbar');

    toolbar.addHandler('image', () => {
      if (this.readonly) {
        this.messageService.add({
          severity: 'warn',
          summary: '提示',
          detail: '唯讀模式無法上傳圖片'
        });
        return;
      }
      this.selectLocalImage();
    });
  }

  private selectLocalImage() {
    if (this.fileInput) {
      this.fileInput.nativeElement.click();
    }
  }

  async onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      this.messageService.add({
        severity: 'error',
        summary: '錯誤',
        detail: '只能上傳圖片檔案'
      });
      input.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: '錯誤',
        detail: '圖片大小不能超過 5MB'
      });
      input.value = '';
      return;
    }

    try {
      this.messageService.add({
        severity: 'info',
        summary: '處理中',
        detail: '正在壓縮圖片...',
        life: 2000
      });

      const compressedFile = await this.compressImageWithCanvas(file);

      this.messageService.clear();
      this.messageService.add({
        severity: 'info',
        summary: '上傳中',
        detail: '圖片上傳中，請稍候...'
      });

    this.imageService.uploadImage(compressedFile, this.permissionCode).subscribe({
        next: (response) => {
          const imageUrl = response.url || this.imageService.getImageUrl(response.sn);
          this.insertImageToEditor(imageUrl);

          this.messageService.clear();
          this.messageService.add({
            severity: 'success',
            summary: '成功',
            detail: `圖片上傳成功（${response.fileSize}）`
          });

          input.value = '';
        },
        error: (error) => {
          console.error('Upload image failed', error);
          this.messageService.clear();
          this.messageService.add({
            severity: 'error',
            summary: '錯誤',
            detail: '圖片上傳失敗：' + (error.error?.error || error.message)
          });
          input.value = '';
        }
      });

    } catch (error) {
      console.error('Compress image failed', error);
      this.messageService.clear();
      this.messageService.add({
        severity: 'error',
        summary: '錯誤',
        detail: '圖片處理失敗'
      });
      input.value = '';
    }
  }

  private insertImageToEditor(imageUrl: string) {
    if (!this.quillInstance) return;

    const range = this.quillInstance.getSelection();
    const safeSrc = 'about:blank';
    const imageValue = { src: safeSrc, originalSrc: imageUrl };

    if (!range) {
      const length = this.quillInstance.getLength();
      this.quillInstance.insertEmbed(length - 1, 'image', imageValue);
      this.quillInstance.setSelection(length, 0);
    } else {
      this.quillInstance.insertEmbed(range.index, 'image', imageValue);
      this.quillInstance.setSelection(range.index + 1, 0);
    }

    requestAnimationFrame(() => {
      if (!this.quillInstance) return;
      const insertedImg = this.quillInstance.root.querySelector(
        'img[src="about:blank"]:not([data-original-src]), img[data-original-src="about:blank"], img[dwa-original-src="about:blank"]'
      ) as HTMLImageElement | null;
      if (!insertedImg) return;

      insertedImg.setAttribute('data-original-src', imageUrl);
      if (insertedImg.hasAttribute('dwa-original-src')) {
        insertedImg.removeAttribute('dwa-original-src');
      }
      insertedImg.setAttribute('src', safeSrc);
      this.loadImageAsBlob(imageUrl, insertedImg);
    });

    setTimeout(() => {
      this.ensureAllImagesStyled();
      this.convertEditorImagesToBlobs();
      this.triggerQuillTextChange();
    }, 150);
  }

  private triggerQuillTextChange(): void {
    if (!this.quillInstance) return;

    const currentSelection = this.quillInstance.getSelection();
    const insertIndex = currentSelection?.index || this.quillInstance.getLength() - 1;

    this.quillInstance.insertText(insertIndex, ' ', 'silent');

    setTimeout(() => {
      this.quillInstance?.deleteText(insertIndex, 1, 'user');
    }, 10);
  }

  /**
   * Editor 內可以用 blob URL 顯示圖片，但真正儲存時要保留可長期存取的 API URL。
   */
  private prepareEditorHtmlForProtectedImages(html: any): any {
    if (typeof html !== 'string' || !this.containsProtectedImage(html)) {
      return html;
    }

    const wrapper = document.createElement('div');
    wrapper.innerHTML = html;

    const images = wrapper.querySelectorAll('img');
    images.forEach((img: HTMLImageElement) => {
      const src = img.getAttribute('src') || '';
      if (!src || !this.isProtectedImageUrl(src)) {
        return;
      }

      img.setAttribute('data-original-src', src);
      img.setAttribute('src', 'about:blank');
    });

    return wrapper.innerHTML;
  }
  private getPersistedHtml(): string {
    if (!this.quillInstance) {
      return this.value || '';
    }

    const clonedRoot = this.quillInstance.root.cloneNode(true) as HTMLElement;
    const images = clonedRoot.querySelectorAll('img');

    images.forEach((img: HTMLImageElement) => {
      const src = img.getAttribute('src') || '';
      const originalSrc = img.getAttribute('data-original-src') || '';

      if ((src.startsWith('blob:') || src === 'about:blank') && originalSrc) {
        img.setAttribute('src', originalSrc);
      }

      img.removeAttribute('data-blob-loading');
      img.removeAttribute('data-original-src');
    });

    return clonedRoot.innerHTML;
  }

  private isProtectedImageUrl(url: string): boolean {
    return url.includes('/HelpCenterImage/images/') || url.includes('/guide-center/v1/images/');
  }

  private containsProtectedImage(html: string): boolean {
    return html.includes('/HelpCenterImage/images/') || html.includes('/guide-center/v1/images/');
  }

  private resolveProtectedImageUrl(url: string): string {
    return this.imageService.resolveImageRequestUrl(url);
  }

  private compressImageWithCanvas(file: File): Promise<File> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const img = new Image();

        img.onload = () => {
          const maxWidth = 800;
          const maxHeight = 800;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height = Math.round((height * maxWidth) / width);
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = Math.round((width * maxHeight) / height);
              height = maxHeight;
            }
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Canvas context not available'));
            return;
          }

          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Canvas to Blob failed'));
                return;
              }

              const compressedFile = new File(
                [blob],
                file.name.replace(/\.\w+$/, '.jpg'),
                { type: 'image/jpeg' }
              );

              resolve(compressedFile);
            },
            'image/jpeg',
            0.85
          );
        };

        img.onerror = () => reject(new Error('Image load failed'));
        img.src = e.target.result;
      };

      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.readAsDataURL(file);
    });
  }
}
